import { FC, useEffect, useState } from 'react';

import clickmeVideoPortfolioCancelSelectionButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/clickme_video_portfolio_cancel_selection_button_click';
import clickmeVideoPortfolioPlayButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/clickme_video_portfolio_play_button_click';
import { GridColumn, Link, Text, VSpacing } from '@hh.ru/magritte-ui';
import { PlayFilledSize24 } from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

import { BannerVideoModal } from 'components/common/BannerVideo/BannerVideoModal/BannerVideoModal';
import withErrorBoundary from 'components/common/ErrorBoundary/withErrorBoundary';

import { resetBannerVideo, resetBannerVideoFiles } from 'store/models/createBanner';
import { resetSelectedVideo } from 'store/models/videoGallery';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerVideo/UploadedVideo/UploadedVideo.module.less';

const TrlKeys = {
    title: 'AdCreate.AdForm.BannerVideo.UploadedVideo.title',
    cancel: 'AdCreate.AdForm.BannerVideo.UploadedVideo.cancel',
};

const UploadedVideoComponent: FC = () => {
    const [isModalVisible, setIsModalVisibleOn, setIsModalVisibleOff] = useOnOffState(false);
    const [thumbnailDataUrl, setThumbnailDataUrl] = useState('');
    const [videoDataUrl, setVideoDataUrl] = useState('');

    const { bannerVideo, bannerVideoFiles } = useSelector((state) => state.createBanner);

    const isVerticalVideo = (bannerVideo?.height ?? 0) > (bannerVideo?.width ?? 0);

    useEffect(() => {
        if (bannerVideoFiles.thumbnail) {
            setThumbnailDataUrl(URL.createObjectURL(bannerVideoFiles.thumbnail));
        }
        if (bannerVideoFiles.video) {
            setVideoDataUrl(URL.createObjectURL(bannerVideoFiles.video));
        }

        return () => {
            URL.revokeObjectURL(videoDataUrl);
            URL.revokeObjectURL(thumbnailDataUrl);
        };
        // указание зависимостей приведет к бесконечному ререндеру
        // поскольку createObjectURL генерирует каждый раз новую строку
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dispatch = useDispatch();
    const trls = useTrls();

    const handlePreviewClick = () => {
        clickmeVideoPortfolioPlayButtonClick();
        setIsModalVisibleOn();
    };

    const handleCancelClick = () => {
        clickmeVideoPortfolioCancelSelectionButtonClick();
        dispatch([resetBannerVideo(null), resetBannerVideoFiles(null), resetSelectedVideo(null)]);
    };

    return (
        <>
            {bannerVideo && (
                <>
                    <Text typography="label-2-regular" style="secondary">
                        {trls(TrlKeys.title)}
                    </Text>
                    <VSpacing default={8} />
                    <GridColumn xs={4} s={isVerticalVideo ? 3 : 5} l={isVerticalVideo ? 2 : 4}>
                        <div className={styles.wrapper} onClick={handlePreviewClick}>
                            <img
                                className={styles.preview}
                                src={thumbnailDataUrl || bannerVideo.thumbnailUrl}
                                alt="banner-video-thumbnail"
                            />
                            <div className={styles.playIconWrapper}>
                                <PlayFilledSize24 initialColor="contrast" />
                            </div>
                        </div>
                        <VSpacing default={12} />
                        <div className={styles.footer}>
                            <Text style="secondary">{`${bannerVideo.width ?? 0} × ${bannerVideo.height ?? 0}`}</Text>
                            <Link
                                inline
                                style="negative"
                                enableVisited={false}
                                Element="button"
                                onClick={handleCancelClick}
                                data-qa="uploaded-video-delete-button"
                            >
                                {trls(TrlKeys.cancel)}
                            </Link>
                        </div>
                    </GridColumn>
                    <BannerVideoModal
                        visible={isModalVisible}
                        onClose={setIsModalVisibleOff}
                        videoUrl={bannerVideo.videoUrl || videoDataUrl}
                        thumbnailUrl={bannerVideo.thumbnailUrl || thumbnailDataUrl}
                        height={bannerVideo.height ?? 0}
                        width={bannerVideo.width ?? 0}
                    />
                </>
            )}
        </>
    );
};

const UploadedVideo = withErrorBoundary(UploadedVideoComponent);
export { UploadedVideo };
